import type { FiltersContext } from '@openx/components-filters/lib/context/types';
import { FILTER_TYPES } from '@openx/components-filters/lib/FiltersBar/types';
import type { TFunction } from 'i18next';
import type { DirectDealsListFilters } from '../../types';
import { getFormattedCountryOptions } from '../utils/getFormattedCountryOptions';

export const getFiltersConfig = (t: TFunction): FiltersContext<DirectDealsListFilters>['filtersConfig'] => [
  {
    key: 'cpm_range',
    name: t('CPM Range'),
    subName: t('Monthly'),
    type: FILTER_TYPES.RANGE,
    config: { inputType: 'float', precision: 2, startAdornment: '$' },
  },
  {
    key: 'unique_visitors',
    name: t('Unique Visitors'),
    subName: t('Monthly'),
    type: FILTER_TYPES.RANGE,
    config: { inputType: 'integer', endAdornment: 'M' },
  },
  {
    key: 'formats',
    name: t('Format'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'Banner', name: t('Banner') },
      { id: 'Video', name: t('Video') },
      { id: 'Native', name: t('Native') },
    ],
  },
  {
    key: 'devices',
    name: t('Device'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'Desktop', name: t('Desktop') },
      { id: 'Mobile', name: t('Mobile') },
      { id: 'Tablet', name: t('Tablet') },
      { id: 'CTV', name: t('CTV') },
      { id: 'OTT', name: t('OTT') },
    ],
  },
  {
    key: 'country',
    name: t('Country'),
    type: FILTER_TYPES.AUTOCOMPLETE,
    options: getFormattedCountryOptions(),
    config: {
      // Limiting options dropdown height to not cover 'Apply' filters action
      dropdownHeight: '220px',
    },
  },
  {
    key: 'audience_distribution',
    name: t('Audience Distribution'),
    type: FILTER_TYPES.GREATER_LESS,
    options: [
      { id: 'female', name: t('Female') },
      { id: 'male', name: t('Male') },
      { id: 'other', name: t('Other') },
    ],
    config: {
      criteriaValueSuffix: '%',
      emptyPlaceHolder: '',
      // Only allow values between 0 to 100 (as percents)
      valueChangeValidator: (value: string): boolean => {
        const num = parseInt(value, 10);
        return !Number.isNaN(num) && num >= 0 && num <= 100;
      },
    },
  },
  {
    key: 'impressions',
    name: t('Impressions'),
    subName: t('Monthly'),
    type: FILTER_TYPES.RANGE,
    config: { inputType: 'integer', endAdornment: 'M' },
  },
  {
    key: 'channels',
    name: t('Channel'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'Web', name: t('Web') },
      { id: 'App', name: t('App') },
      { id: 'CTV', name: t('CTV') },
      { id: 'Audio', name: t('Audio') },
      { id: 'OOH', name: t('OOH') },
    ],
  },
  {
    key: 'deal_types',
    name: t('Deal Type'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'Private Marketplaces (PMP)', name: t('Private Marketplaces (PMP)') },
      { id: 'Programmatic Guaranteed (PG)', name: t('Programmatic Guaranteed (PG)') },
      { id: 'Auction Packages', name: t('Auction Packages') },
      { id: 'Preferred Deals', name: t('Preferred Deals') },
    ],
  },
  {
    key: 'sensitive_categories',
    name: t('Sensitive Categories Allowed'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'Gambling', name: t('Gambling') },
      { id: 'Pharmaceuticals', name: t('Pharmaceuticals') },
      { id: 'Tobacco', name: t('Tobacco') },
      { id: 'Political', name: t('Political') },
      { id: 'Cannabis', name: t('Cannabis') },
      { id: 'Alcohol', name: t('Alcohol') },
      { id: 'Firearms', name: t('Firearms') },
    ],
  },
  {
    key: 'geo_region',
    name: t('Geo Region'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'US', name: t('US') },
      { id: 'Canada', name: t('Canada') },
      { id: 'EMEA', name: t('EMEA') },
      { id: 'LATAM', name: t('LATAM') },
      { id: 'APAC', name: t('APAC') },
    ],
  },
  {
    key: 'ctv_type',
    name: t('CTV type'),
    type: FILTER_TYPES.CHECKBOX,
    options: [
      { id: 'FAST', name: t('FAST') },
      { id: 'vMVPD', name: t('vMVPD') },
      { id: 'Owned & Operated', name: t('Owned & Operated') },
      { id: 'AVOD/SVOD', name: t('AVOD/SVOD') },
    ],
  },
];
